import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../../contexts/AuthContext';
import { libraryService } from '../../services/libraryService';
import type { Comment } from '../../types';
import { useTheme } from '@flowcore-water/ui';

interface CommentSectionProps {
  libraryItemId: string;
}

export const CommentSection: React.FC<CommentSectionProps> = ({ libraryItemId }) => {
  const { t } = useTheme();
  const { user } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    libraryService.getComments(libraryItemId).then((c) => {
      setComments(c);
      setLoading(false);
    });
  }, [libraryItemId]);

  const handlePost = async () => {
    if (!newComment.trim() || !user) return;
    setPosting(true);
    try {
      const comment = await libraryService.addComment(
        libraryItemId,
        user.uid,
        user.displayName,
        newComment.trim()
      );
      setComments((prev) => [...prev, comment]);
      setNewComment('');
    } catch (err) {
      console.error('Error posting comment:', err);
    } finally {
      setPosting(false);
    }
  };

  const handleDelete = async (commentId: string) => {
    try {
      await libraryService.deleteComment(libraryItemId, commentId, user!.uid);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
    } catch (err) {
      console.error('Error deleting comment:', err);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold " style={{ color: t.textPrimary }}>Comments</h3>

      {loading ? (
        <p className="text-sm " style={{ color: t.textMuted }}>Loading comments...</p>
      ) : comments.length === 0 ? (
        <p className="text-sm  italic" style={{ color: t.textMuted }}>No comments yet</p>
      ) : (
        <div className="space-y-3">
          {comments.map((comment) => (
            <div
              key={comment.id}
              className="rounded-lg border /60 p-4"
              style={{ background: '#0a0f1a' }}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold " style={{ color: t.accent }}>
                    {comment.userName || 'Unknown'}
                  </span>
                  <span className="text-xs " style={{ color: t.textMuted }}>
                    {new Date(comment.createdAt).toLocaleDateString()}{' '}
                    {new Date(comment.createdAt).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
                {user && (user.uid === comment.userId || user.role === 'admin') && (
                  <button
                    onClick={() => handleDelete(comment.id)}
                    className="text-xs hover:opacity-80 transition-colors" style={{ color: t.textMuted }}
                  >
                    Delete
                  </button>
                )}
              </div>
              <div className="prose prose-invert prose-sm max-w-none " style={{ color: t.textSecondary }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {comment.body}
                </ReactMarkdown>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* New comment form */}
      {user && (
        <div className="space-y-2">
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Add a comment (markdown supported)..."
            rows={3}
            className="w-full rounded-lg border px-3 py-2 text-sm placeholder-slate-500 focus:border-cyan-500 focus:outline-none resize-none"
            style={{ color: t.textSecondary, background: t.pageBg }}
          />
          <div className="flex justify-end">
            <button
              onClick={handlePost}
              disabled={posting || !newComment.trim()}
              className="rounded-lg bg-cyan-600 px-4 py-1.5 text-sm font-medium  hover:bg-cyan-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" style={{ color: t.textPrimary }}
            >
              {posting ? 'Posting...' : 'Post Comment'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
